import React, { useCallback, useEffect, useState } from 'react';
import Skeleton from '../../../components/common/Skeleton';
import NoDataFound from '../../../components/common/NoDataFound';
import Toast, { ToastMessage } from '../../../components/common/Toast';
import { formatDate } from '../../../components/common/FormControl';
import LitInstanceDetail from '../../../components/compliances/lit-instances/LitInstanceDetail';
import { AlertTriangle, Clock, Plus } from 'lucide-react';
import Button from '../../../components/common/Button';
import { litInstanceService, type LitInstance } from '../../../services/litInstanceService';
import { visibleForScope, isClosedLitigation } from './workArchive';

const STATUS_STYLE: Record<string, string> = {
  Open:          'bg-red-50 text-red-700 border-red-200',
  'In Progress': 'bg-amber-50 text-amber-700 border-amber-200',
  Closed:        'bg-gray-100 text-gray-500 border-gray-200',
};

function dueBadge(dateStr: string | null) {
  if (!dateStr) return <span className="text-[11px] text-gray-400">—</span>;
  const days = Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86_400_000);
  if (days < 0)  return <span className="text-[10px] bg-red-50 text-red-600 border border-red-200 px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5"><AlertTriangle size={9} />Overdue</span>;
  if (days <= 7) return <span className="text-[10px] bg-amber-50 text-amber-600 border border-amber-200 px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5"><Clock size={9} />{days}d</span>;
  return <span className="text-[11px] text-gray-600">{formatDate(dateStr)}</span>;
}

interface LitWorkBodyProps {
  scope?: 'active' | 'archive';
  readOnly?: boolean;
}

const LitWorkBody: React.FC<LitWorkBodyProps> = ({ scope = 'active', readOnly = false }) => {
  const [rows,      setRows]      = useState<LitInstance[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [toast,     setToast]     = useState<ToastMessage | null>(null);
  const [selected,  setSelected]  = useState<LitInstance | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await litInstanceService.getMine()); }
    catch (e: any) { setToast({ type: 'error', message: e?.message || 'Failed to load litigations' }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = (r: LitInstance) => { setSelected(r); setModalOpen(true); };

  // History (archive) keeps only closed/resolved litigations; Action Centre is unchanged.
  const scoped = visibleForScope(rows, (r) => isClosedLitigation(r.status), scope);

  return (
    <div className="netbody-card flex flex-col h-full">
      <div className="netbody-header">
        {!readOnly && (
          <div className="ml-auto">
            <Button
              variant="fill"
              icon={Plus}
              onClick={() => { setSelected(null); setModalOpen(true); }}
              data-testid="BTN_lit_work_log_notice"
            >
              Log Notice
            </Button>
          </div>
        )}
      </div>

      <div className="netbody-content">
        <div className="scroll-container flex-1 overflow-hidden">
          <div className="table-list">
            {!loading && scoped.length === 0 ? (
              <NoDataFound
                title={scope === 'archive' ? 'No Closed Litigations' : 'No Litigations Assigned'}
                message={scope === 'archive'
                  ? 'Resolved or closed litigations will appear here.'
                  : 'Litigations assigned to your position will appear here.'}
              />
            ) : (
              <div className="table-list-scroll">
                <table className="table-list-table">
                  <thead>
                    <tr>
                      <th className="table-list-th table-list-cell--sno">S.No</th>
                      <th className="table-list-th">Notice ID</th>
                      <th className="table-list-th">Act / Law</th>
                      <th className="table-list-th">Current Step</th>
                      <th className="table-list-th">Next Due</th>
                      <th className="table-list-th">Status</th>
                    </tr>
                  </thead>
                  <tbody aria-busy={loading} aria-live="polite">
                    {loading && <Skeleton.TableRows cols={['3xs', 'md', 'md', 'md', 'sm', 'pill']} />}
                    {!loading && scoped.map((r, idx) => (
                      <tr key={r.id} className="table-list-row"
                        onClick={() => open(r)}
                        data-testid={`ROW_my_lit_${r.id}`}>
                        <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                        <td className="table-list-cell">
                          <span className="table-list-cell-title">{r.notice_id}</span>
                          {r.issued_by && <span className="table-list-cell-sub">{r.issued_by}</span>}
                        </td>
                        <td className="table-list-cell table-list-cell--truncate">
                          {r.law_name || r.act || '—'}
                        </td>
                        <td className="table-list-cell">
                          {r.current_step_label
                            ? <span className="text-xs text-gray-700">{r.current_step_label}</span>
                            : <span className="text-[11px] italic text-gray-300">not started</span>}
                        </td>
                        <td className="table-list-cell">
                          {dueBadge(r.next_due_date)}
                        </td>
                        <td className="table-list-cell">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_STYLE[r.status] || STATUS_STYLE['Open']}`}>
                            {r.status || 'Open'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <LitInstanceDetail
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={load}
        instance={selected}
        readOnly={readOnly}
        mode="execute"
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default LitWorkBody;
